import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { DESCRIPTION_CHARACTER_LIMIT } from '../utils/descriptionLimit';
import { docToMarkdown } from '../utils/stateToMarkdown';
import { descriptionChangeAllowed } from '../utils/descriptionText';

const descriptionCharacterLimitKey = new PluginKey('descriptionCharacterLimit');

export type DescriptionCharacterLimitOptions = {
  limit: number;
  onStorageLimit?: () => void;
};

export function createAllowDescriptionTransaction(limit: number, onStorageLimit?: () => void) {
  return (tr: Transaction, state: EditorState): boolean => {
    if (!tr.docChanged) return true;
    // Use tr.doc directly — state.apply(tr) re-enters filterTransaction.
    const next = docToMarkdown(tr.doc, state.schema);
    const previous = docToMarkdown(state.doc, state.schema);
    const allowed = descriptionChangeAllowed(next, previous, limit);
    if (!allowed && Array.from(next).length > 4000 && Array.from(next).length > Array.from(previous).length) {
      onStorageLimit?.();
    }
    return allowed;
  };
}

/**
 * Blocks document changes that exceed the visible or stored character limits.
 * Shrinks are still allowed when the description is already over the limit.
 */
export const DescriptionCharacterLimit = Extension.create<DescriptionCharacterLimitOptions>({
  name: 'descriptionCharacterLimit',

  addOptions() {
    return {
      limit: DESCRIPTION_CHARACTER_LIMIT
    };
  },

  addProseMirrorPlugins() {
    const allow = createAllowDescriptionTransaction(this.options.limit, this.options.onStorageLimit);
    return [
      new Plugin({
        key: descriptionCharacterLimitKey,
        filterTransaction: allow
      })
    ];
  }
});
